import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { platformAPI } from '../../services/api'
import { t, formatDate, formatDateTime } from '../../i18n'
import { PageHeader, Table, Loading, Empty, Chip, StatCard, money, cents } from './ui'

const INVOICE_TONE = { paid: 'green', open: 'blue', draft: 'gray', uncollectible: 'red', void: 'gray' }

export default function PlatformPayments() {
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [tab, setTab] = useState('subscriptions')

  useEffect(() => {
    platformAPI.getPayments().then(r => setData(r.data)).catch(e => setError(e.response?.data?.error || 'Failed to load payments'))
  }, [])

  if (error) return <Empty>{t(error)}</Empty>
  if (!data) return <Loading />

  const totals = status => data.invoiceTotals.filter(x => x.status === status).map(x => cents(x.cents, x.currency)).join(' + ') || '—'

  return (
    <div>
      <PageHeader title={t('Payments')} subtitle={t('What businesses pay you, and what their customers pay them.')} />

      <div className="mb-4 inline-flex rounded-lg bg-white p-1 ring-1 ring-slate-200">
        {[['subscriptions', 'Subscription invoices'], ['sales', 'Store sales']].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} className={`rounded-md px-3 py-1.5 text-sm font-medium ${tab === key ? 'bg-slate-900 text-white' : 'text-slate-600 hover:text-slate-900'}`}>{t(label)}</button>
        ))}
      </div>

      {tab === 'subscriptions' ? (
        <>
          <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <StatCard label={t('Paid')} value={totals('paid')} />
            <StatCard label={t('Open')} value={totals('open')} />
            <StatCard label={t('Failed')} value={totals('uncollectible')} />
          </div>
          {!data.invoices.length ? (
            <Empty>{t('No subscription invoices yet. They appear here once Stripe is connected and businesses subscribe.')}</Empty>
          ) : (
            <Table head={[t('Business'), t('Amount'), t('Status'), t('Period'), t('Date'), '']}>
              {data.invoices.map(inv => (
                <tr key={inv.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3"><Link to={`/platform/businesses?open=${inv.branch_id}`} className="font-medium text-slate-900 hover:text-indigo-600">{inv.business_name || `#${inv.branch_id}`}</Link></td>
                  <td className="px-4 py-3 tabular-nums">{cents(inv.amount_cents, (inv.currency || 'usd').toUpperCase())}</td>
                  <td className="px-4 py-3"><Chip tone={INVOICE_TONE[inv.status]}>{t(inv.status)}</Chip></td>
                  <td className="whitespace-nowrap px-4 py-3 text-slate-500">{inv.period_start ? `${formatDate(inv.period_start)} – ${formatDate(inv.period_end)}` : '—'}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-slate-500">{formatDate(inv.created_at)}</td>
                  <td className="px-4 py-3 text-end">{inv.hosted_invoice_url && <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer" className="text-xs font-medium text-indigo-600">{t('View invoice')}</a>}</td>
                </tr>
              ))}
            </Table>
          )}
        </>
      ) : !data.sales.length ? <Empty>{t('No payments yet.')}</Empty> : (
        <Table head={[t('Business'), t('Order'), t('Method'), t('Amount'), t('Date')]}>
          {data.sales.map(p => (
            <tr key={p.id} className="hover:bg-slate-50">
              <td className="px-4 py-3"><Link to={`/platform/businesses?open=${p.branch_id}`} className="font-medium text-slate-900 hover:text-indigo-600">{p.business_name}</Link></td>
              <td className="px-4 py-3 text-slate-600">{p.order_code || `#${p.order_id}`}</td>
              <td className="px-4 py-3"><Chip tone={p.payment_type === 'REFUND' ? 'red' : 'gray'}>{t(p.payment_type)}</Chip></td>
              <td className={`px-4 py-3 tabular-nums ${p.payment_type === 'REFUND' ? 'text-rose-600' : ''}`}>{money(p.amount, p.currency)}</td>
              <td className="whitespace-nowrap px-4 py-3 text-slate-500">{formatDateTime(p.paid_at)}</td>
            </tr>
          ))}
        </Table>
      )}
    </div>
  )
}
